import { useState, useCallback, useEffect } from "react"
import { Loader2 } from "lucide-react"
import { GradientSparkleIcon } from "@/components/extraction/GradientSparkleIcon"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { TextWithCitations, type CitationSource } from "@/components/drafting/CitationBadge"
import { usePreviewStore } from "@/stores/preview-store"
import { useStrategyStore } from "@/stores/strategy-store"
import type { WinTheme, ThemeSource } from "@/stores/strategy-store"

const QUICK_CHIPS = [
  'More outcomes-focused',
  'Stronger evidence',
  'Shorten',
  'Less jargon',
  'Tie to client values',
]

const categoryLabels: Record<string, string> = {
  experience: 'Experience',
  personnel: 'Personnel',
  methodology: 'Methodology',
  innovation: 'Innovation',
  relationship: 'Relationship',
  value: 'Value',
  risk: 'Risk Mitigation',
  local_presence: 'Local Presence',
  other: 'Other',
}

function toCitationSources(sources: ThemeSource[] | null | undefined): CitationSource[] {
  return (sources ?? []).map(s => ({
    documentId: s.documentId,
    documentName: s.documentName,
    pageNumber: s.pageNumber,
  }))
}

interface WinThemeRefineDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  theme: WinTheme
  bidId: string
}

type Phase = 'compose' | 'refining' | 'review'

export function WinThemeRefineDialog({ open, onOpenChange, theme, bidId }: WinThemeRefineDialogProps) {
  const { refineTheme, acceptRefinement, dismissRefinement, isSavingTheme } = useStrategyStore()

  // Track refine state from the live store (theme object updates via polling)
  const liveTheme = useStrategyStore(s => s.themes.find(t => t.id === theme.id) ?? theme)

  const [instruction, setInstruction] = useState('')
  const [phase, setPhase] = useState<Phase>('compose')

  // Sync phase from live theme state (polling updates the store, we react here)
  useEffect(() => {
    if (liveTheme.refineStatus === 'refining') {
      setPhase('refining')
    } else if (liveTheme.refineStatus === 'completed' && liveTheme.refineResult) {
      setPhase('review')
      // Pre-fill instruction from the stored result for "Refine again"
      if (liveTheme.refineResult.instruction && !instruction) {
        setInstruction(liveTheme.refineResult.instruction)
      }
    } else if (liveTheme.refineStatus === 'failed') {
      setPhase('compose')
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveTheme.refineStatus, liveTheme.refineResult])

  // Reset on open/close
  useEffect(() => {
    if (!open) {
      setPhase('compose')
      setInstruction('')
    }
  }, [open])

  const handleRefine = useCallback(() => {
    if (!instruction.trim()) return
    void refineTheme(bidId, theme.id, instruction.trim())
  }, [bidId, theme.id, instruction, refineTheme])

  const handleAccept = useCallback(async () => {
    await acceptRefinement(bidId, theme.id)
    onOpenChange(false)
  }, [bidId, theme.id, acceptRefinement, onOpenChange])

  const handleDiscard = useCallback(async () => {
    await dismissRefinement(bidId, theme.id)
    onOpenChange(false)
  }, [bidId, theme.id, dismissRefinement, onOpenChange])

  const handleRefineAgain = useCallback(() => {
    // Keep instruction pre-filled from the last run, go back to compose
    const lastInstruction = liveTheme.refineResult?.instruction ?? instruction
    setInstruction(lastInstruction)
    setPhase('compose')
  }, [liveTheme.refineResult, instruction])

  const handleNavigateToSource = useCallback((source: CitationSource) => {
    void usePreviewStore.getState().openPreview({
      documentId: source.documentId,
      documentName: source.documentName,
      ...(source.pageNumber != null && { page: source.pageNumber }),
      context: 'library',
      citationLabel: `Source: ${source.documentName}`,
    })
  }, [])

  const refined = liveTheme.refineResult
  const refinedSources = toCitationSources(refined?.sources)
  const currentSources = toCitationSources(theme.sources)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GradientSparkleIcon className="h-4 w-4" />
            Refine with AI
          </DialogTitle>
        </DialogHeader>

        {/* COMPOSE */}
        {phase === 'compose' && (
          <div className="space-y-4">
            <div className="text-meta border border-border rounded-lg px-3 py-2 bg-muted/30">
              <span className="font-medium text-foreground">{theme.title}</span>
              <span className="mx-1.5">·</span>
              {categoryLabels[theme.category] ?? theme.category}
            </div>

            <div>
              <label className="text-xs font-medium mb-1.5 block">How should AI change this theme?</label>
              <Textarea
                value={instruction}
                onChange={e => setInstruction(e.target.value)}
                placeholder="e.g. Tie this more directly to the client's sustainability goals, or find stronger evidence from our recent projects…"
                rows={4}
                className="resize-none text-sm focus-visible:ring-[hsl(var(--ai-accent))]"
                onKeyDown={e => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault()
                    handleRefine()
                  }
                }}
              />
            </div>

            <div>
              <div className="text-meta mb-2">Quick refinements</div>
              <div className="flex flex-wrap gap-1.5">
                {QUICK_CHIPS.map(chip => (
                  <button
                    key={chip}
                    className="text-xs px-2.5 py-1 rounded-full border border-border bg-card hover:bg-[hsl(var(--ai-accent-soft))] hover:border-[hsl(var(--ai-accent)/0.4)] hover:text-[hsl(var(--ai-accent-text))] transition-colors"
                    onClick={() => setInstruction(prev => prev ? `${prev} ${chip.toLowerCase()}` : chip)}
                  >
                    {chip}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-border">
              <span className="text-meta">Original kept until you accept</span>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
                <Button
                  size="sm"
                  disabled={!instruction.trim()}
                  onClick={handleRefine}
                >
                  <GradientSparkleIcon className="h-3.5 w-3.5 mr-1.5" />
                  Refine
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* REFINING */}
        {phase === 'refining' && (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--ai-accent))]" />
            <p className="text-sm text-muted-foreground">Refining theme…</p>
            <p className="text-meta">This takes 15–30 seconds</p>
          </div>
        )}

        {/* REVIEW */}
        {phase === 'review' && refined && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {/* Current */}
              <div>
                <div className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground inline-block" />
                  Current
                </div>
                <div className="border border-border rounded-xl p-4 bg-muted/20 space-y-2">
                  <div className="text-sm font-medium">{theme.title}</div>
                  <div className="text-meta">{categoryLabels[theme.category] ?? theme.category}</div>
                  {theme.description && (
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {currentSources.length > 0
                        ? <TextWithCitations text={theme.description} sources={currentSources} onNavigateToSource={handleNavigateToSource} />
                        : theme.description
                      }
                    </p>
                  )}
                </div>
              </div>

              {/* Refined */}
              <div>
                <div className="text-xs font-medium mb-2 flex items-center gap-1.5" style={{ color: 'hsl(var(--ai-accent-text))' }}>
                  <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: 'hsl(var(--ai-accent))' }} />
                  Refined
                </div>
                <div className="border rounded-xl p-4 space-y-2" style={{ borderColor: 'hsl(var(--ai-accent) / 0.3)', background: 'hsl(var(--ai-accent-soft) / 0.3)' }}>
                  <div className="text-sm font-medium">{refined.title}</div>
                  <div className="text-meta">{categoryLabels[refined.category] ?? refined.category}</div>
                  {refined.description && (
                    <p className="text-sm leading-relaxed">
                      {refinedSources.length > 0
                        ? <TextWithCitations text={refined.description} sources={refinedSources} onNavigateToSource={handleNavigateToSource} />
                        : refined.description
                      }
                    </p>
                  )}
                  {refined.sources.length > 0 && (
                    <div className="text-meta pt-1 border-t border-border/50">
                      {refined.sources.length} source{refined.sources.length !== 1 ? 's' : ''} linked
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-border">
              <Button variant="ghost" size="sm" onClick={handleRefineAgain}>Refine again</Button>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => void handleDiscard()}>Discard</Button>
                <Button size="sm" onClick={() => void handleAccept()} disabled={isSavingTheme}>
                  {isSavingTheme ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Applying…</> : 'Accept changes'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
